import {useState} from 'react'
import {faDownload} from '@fortawesome/free-solid-svg-icons'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {HttpError} from '@freckle/educator-entities/ts/common/exceptions/http-error'
import {
  extractDataAndHeaders,
  fancyClient,
  downloadFile,
} from '@freckle/educator-entities/ts/openapi-clients'
import type {IdentityProviderT} from '@freckle/educator-entities/ts/roster/identity-provider'
import {Button} from '@freckle/educator-materials/ts/components/button'
import {ButtonAsync} from '@freckle/educator-materials/ts/components/button-async'
import {Flex} from '@freckle/educator-materials/ts/components/flex'
import {Modal, Header, Body, Footer} from '@freckle/educator-materials/ts/components/modal'
import {H3} from '@freckle/educator-materials/ts/components/typography'
import {Text} from '@freckle/educator-materials/ts/components/typography'
import {useSafeTranslation} from '@freckle/educator-materials/ts/helpers/translate'
import {SafeTrans} from '@freckle/educator-materials/ts/helpers/translate/component'
import {logErrorAsUnhandled} from '@freckle/school/ts/common/helpers/exception-handlers/bugsnag-client'
import Notification from '@freckle/school/ts/common/helpers/notification'
import type {UnmanagedSummaryT} from '@freckle/school/ts/rostering/components/clean-up-roster/clean-up-phase'

type Props = {
  unmanaged: UnmanagedSummaryT
}

export function UnmanagedStudents({unmanaged}: Props) {
  const [showHelpModal, setShowHelpModal] = useState(false)
  const {t} = useSafeTranslation()

  const {numUnmanagedStudents, identityProviders, schoolIds} = unmanaged

  const idp =
    identityProviders.length === 1
      ? t('DISPLAY_IDP', {idp: identityProviders[0]})
      : t('UNMANAGED_STUDENTS_UNKNOWN_IDP_TEXT')

  const downloadStudentsCsv = () =>
    downloadCsv(schoolIds, identityProviders).then(
      () => {},
      error => {
        Notification.displayErrorNotification(t('UNMANAGED_STUDENTS_DOWNLOAD_CSV_ERROR'))
        logErrorAsUnhandled(new HttpError(error))
      },
    )

  return (
    <>
      <H3>{t('UNMANAGED_STUDENTS_TITLE', {idp})}</H3>
      <SafeTrans
        i18nKey="UNMANAGED_STUDENTS_BODY"
        values={{numUnmanagedStudents, idp}}
        components={{
          b: <b />,
          whyButton: <Button style="link" onClick={() => setShowHelpModal(true)} />,
          downloadButton: <ButtonAsync style="link" onClick={() => downloadStudentsCsv()} />,
          downloadIcon: <FontAwesomeIcon icon={faDownload} />,
        }}
      />
      <HelpModal idp={idp} show={showHelpModal} onHide={() => setShowHelpModal(false)} />
    </>
  )
}

function HelpModal({idp, show, onHide}: {idp: string; show: boolean; onHide: () => void}) {
  const {t} = useSafeTranslation()
  const title = t('UNMANAGED_STUDENTS_HELP_MODAL_TITLE', {idp})
  const footer = <Button onClick={onHide}>{t('UNMANAGED_STUDENTS_HELP_MODAL_BUTTON_HIDE')}</Button>

  return (
    <>
      <Modal show={show} onHide={onHide} height="medium-small">
        <Header onClickClose={onHide}>{title}</Header>
        <Body>
          <Flex flexDirection="column">
            <SafeTrans
              i18nKey="UNMANAGED_STUDENTS_HELP_MODAL_BODY"
              components={{
                text: <Text style="body-2" />,
                strong: <strong />,
                ul: <ul />,
                li: <li />,
              }}
            />
          </Flex>
        </Body>
        <Footer>{footer}</Footer>
      </Modal>
    </>
  )
}

function downloadCsv(
  schoolIds: Array<number>,
  identityProviders: Array<IdentityProviderT>,
): Promise<void> {
  return fancyClient
    .GET('/3/students', {
      headers: {
        Accept: 'text/csv',
      },
      parseAs: 'blob',
      params: {
        query: {
          'schools.id': schoolIds,
          'identity-provider[notin]': identityProviders,
        },
      },
    })
    .then(extractDataAndHeaders)
    .then(({data, headers}) => downloadFile(data, headers, 'students.csv'))
}
